import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { authService } from "@/services/authService";
import { gmailService } from "@/services/gmailService";
import type { Report, User } from "@/types";
import type { WeeklyReport } from "@/types/WeeklyReport";
import {
  addWeeks,
  format,
  isWithinInterval,
  startOfWeek,
  subWeeks,
} from "date-fns";
import {
  AlertCircle,
  BarChart3,
  CalendarIcon,
  CheckCircle,
  Eye,
  Mail,
  Search,
  Users,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface ReportComplianceDashboardProps {
  reports: Report[];
  users: User[];
  loading: boolean;
}

interface WeekData {
  startDate: Date;
  endDate: Date;
  label: string;
}

interface UserComplianceData {
  user: { id: string; name: string; email: string };
  weeks: {
    [weekKey: string]: {
      hasSentReport: boolean;
      reports: WeeklyReport[];
    };
  };
  missedWeeks: number;
}

const ReportComplianceDashboard: React.FC<ReportComplianceDashboardProps> = ({
  reports,
  users,
  loading,
}) => {
  const [weeksToShow, setWeeksToShow] = useState<number>(8);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [selectedReport, setSelectedReport] = useState<WeeklyReport | null>(
    null,
  );
  const [reminderUser, setReminderUser] = useState<{
    user: { name: string; email: string };
    week: WeekData;
  } | null>(null);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [sentReminders, setSentReminders] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>("all");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showSentOnly, setShowSentOnly] = useState<boolean>(false);
  const [selectedClient, setSelectedClient] = useState<string>("all");
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [isClientSearchOpen, setIsClientSearchOpen] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [toRecipient, setToRecipient] = useState("");
  const [ccRecipient, setCcRecipient] = useState("");
  const [bccRecipient, setBccRecipient] = useState("");

  // Add click outside handler to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
      if (
        clientDropdownRef.current &&
        !clientDropdownRef.current.contains(event.target as Node)
      ) {
        setIsClientSearchOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Load existing reminder status
  useEffect(() => {
    const loadReminderStatus = async () => {
      if (!currentUser) return;

      try {
        // Create JWT token for authentication
        const token = btoa(JSON.stringify({ email: currentUser.email }));

        const response = await fetch("/api/admin/reminder-status", {
          headers: authService.getAuthHeaders(),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.reminders) {
            const reminderKeys = data.reminders.map((reminder: any) =>
              getReminderKey(reminder.userEmail, reminder.weekLabel),
            );
            setSentReminders(new Set(reminderKeys));
          }
        }
      } catch (error) {
        console.error("Error loading reminder status:", error);
      }
    };

    loadReminderStatus();
  }, [currentUser]);

  // Display text for the selected user
  const selectedUserDisplay = useMemo(() => {
    if (selectedUser === "all") return "All Users";
    const user = users.find((u) => u.email === selectedUser);
    return user ? user.name || user.email : selectedUser;
  }, [selectedUser, users]);

  // Get unique clients from reports
  const uniqueClients = useMemo(() => {
    const clients = new Set(
      reports
        .filter((report) => report.clientName)
        .map((report) => report.clientName),
    );
    return Array.from(clients).sort();
  }, [reports]);

  // Filter clients based on search
  const filteredClients = useMemo(() => {
    if (!clientSearchQuery && !isClientSearchOpen) return [];
    if (!clientSearchQuery && isClientSearchOpen) return uniqueClients;
    return uniqueClients.filter((client) =>
      client.toLowerCase().includes(clientSearchQuery.toLowerCase()),
    );
  }, [uniqueClients, clientSearchQuery, isClientSearchOpen]);

  // Generate weeks data
  const weeks = useMemo(() => {
    const result: WeekData[] = [];
    const today = new Date();
    const baseDate = startDate || today;

    // Start from (weeksToShow - 1) weeks ago
    let currentDate = subWeeks(baseDate, weeksToShow - 1);

    for (let i = 0; i < weeksToShow; i++) {
      // Week starts on Sunday
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 }); // Sunday

      // Week ends on Thursday (4 days after Sunday)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 4); // Thursday (0 = Sunday, 4 = Thursday)

      // Set to end of day
      weekEnd.setHours(23, 59, 59, 999);

      result.push({
        startDate: weekStart,
        endDate: weekEnd,
        label: `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`,
      });

      currentDate = addWeeks(currentDate, 1);
    }

    // Reverse the array to show newest weeks first
    return result.reverse();
  }, [weeksToShow, startDate]);

  // Helper function to check if recipients match filter
  const hasRecipientMatch = (
    recipients: string[] | undefined,
    filter: string,
  ) => {
    if (!filter.trim() || !recipients) return true;
    const filterLower = filter.toLowerCase();
    return recipients.some((r) => r.toLowerCase().includes(filterLower));
  };

  // Filter reports based on search term and recipients
  const filteredReports = useMemo(() => {
    let filtered = reports;

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (report) =>
          report.subject?.toLowerCase().includes(searchLower) ||
          report.createdBy?.toLowerCase().includes(searchLower) ||
          report.date?.includes(searchLower) ||
          report.dateRange?.toLowerCase().includes(searchLower),
      );
    }

    // Filter by recipients
    if (toRecipient.trim()) {
      filtered = filtered.filter((report) =>
        hasRecipientMatch(report.to, toRecipient),
      );
    }
    if (ccRecipient.trim()) {
      filtered = filtered.filter((report) =>
        hasRecipientMatch(report.cc, ccRecipient),
      );
    }
    if (bccRecipient.trim()) {
      filtered = filtered.filter((report) =>
        hasRecipientMatch(report.bcc, bccRecipient),
      );
    }

    return filtered;
  }, [reports, searchTerm, toRecipient, ccRecipient, bccRecipient]);

  // Calculate compliance data for each user
  const complianceData = useMemo(() => {
    return users
      .map((user) => {
        const userData: UserComplianceData = {
          user: { id: user.id, name: user.name, email: user.email },
          weeks: {},
          missedWeeks: 0,
        };

        // Filter reports for this user
        let userReports = filteredReports.filter(
          (report) => report.createdBy === user.email,
        );

        // If client is selected, filter just for that client's reports
        // When filtering by client, we'll show all reports for this client
        // across all weeks to provide a complete view of client-specific history
        if (selectedClient !== "all") {
          userReports = userReports.filter(
            (r) => r.clientName === selectedClient,
          );
        }

        // Initialize all weeks as missed
        weeks.forEach((week) => {
          const weekKey = format(week.startDate, "yyyy-MM-dd");
          // Find all reports for this week using multiple criteria
          const reportsThisWeek = userReports.filter((report) => {
            // Method 1: Check by date field
            if (report.date) {
              try {
                const reportDate = new Date(report.date);
                if (
                  !isNaN(reportDate.getTime()) &&
                  isWithinInterval(reportDate, {
                    start: week.startDate,
                    end: week.endDate,
                  })
                ) {
                  return true;
                }
              } catch (error) {
                console.warn("Error parsing report date:", report.date, error);
              }
            }

            // Method 2: Check by subject containing week information
            if (report.subject) {
              const subject = report.subject.toLowerCase().trim();
              const weekLabel = week.label.toLowerCase().trim();
              // Try to extract a date range from the subject
              const dateRangeMatch = subject.match(
                /([a-z]{3,9} \d{1,2}) ?- ?([a-z]{3,9} \d{1,2}, \d{4})/i,
              );
              if (dateRangeMatch) {
                // Example: 'jun 2 - jun 3, 2025'
                const startStr =
                  dateRangeMatch[1] + ", " + dateRangeMatch[2].split(",")[1];
                const endStr = dateRangeMatch[2];
                const startDate = new Date(startStr);
                const endDate = new Date(endStr);
                if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                  // Check if the week overlaps with the subject's date range
                  if (
                    isWithinInterval(week.startDate, {
                      start: startDate,
                      end: endDate,
                    }) ||
                    isWithinInterval(week.endDate, {
                      start: startDate,
                      end: endDate,
                    }) ||
                    isWithinInterval(startDate, {
                      start: week.startDate,
                      end: week.endDate,
                    })
                  ) {
                    return true;
                  }
                }
              }
              // Fallback: match if subject contains the exact week label
              if (subject.includes(weekLabel)) {
                return true;
              }
            }

            // Method 3: Check by dateRange field (if exists)
            if (report.dateRange) {
              try {
                const dateRange = report.dateRange;
                if (typeof dateRange === "string") {
                  // Try to extract dates from dateRange string
                  const dateMatch = dateRange.match(
                    /(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})/g,
                  );
                  if (dateMatch && dateMatch.length >= 2) {
                    const startDate = new Date(dateMatch[0]);
                    const endDate = new Date(dateMatch[1]);
                    if (
                      !isNaN(startDate.getTime()) &&
                      !isNaN(endDate.getTime())
                    ) {
                      // Check if the week overlaps with the report's date range
                      if (
                        isWithinInterval(week.startDate, {
                          start: startDate,
                          end: endDate,
                        }) ||
                        isWithinInterval(week.endDate, {
                          start: startDate,
                          end: endDate,
                        }) ||
                        isWithinInterval(startDate, {
                          start: week.startDate,
                          end: week.endDate,
                        })
                      ) {
                        return true;
                      }
                    }
                  }
                }
              } catch (error) {
                console.warn(
                  "Error parsing dateRange:",
                  report.dateRange,
                  error,
                );
              }
            }

            // Method 4: Check by createdAt date (fallback)
            if (report.createdAt) {
              try {
                const createdDate = new Date(report.createdAt);
                if (
                  !isNaN(createdDate.getTime()) &&
                  isWithinInterval(createdDate, {
                    start: week.startDate,
                    end: week.endDate,
                  })
                ) {
                  return true;
                }
              } catch (error) {
                console.warn(
                  "Error parsing createdAt date:",
                  report.createdAt,
                  error,
                );
              }
            }

            return false;
          });

          // Only count as sent if at least one report is actually sent (sentByEmail === true)
          const hasSent = reportsThisWeek.some((r) => r.sentByEmail === true);
          userData.weeks[weekKey] = {
            hasSentReport: hasSent,
            reports: reportsThisWeek,
          };
        });

        // Count missed weeks (no sent report)
        userData.missedWeeks = Object.values(userData.weeks).filter(
          (week) => !week.hasSentReport,
        ).length;

        return userData;
      })
      .sort((a, b) => b.missedWeeks - a.missedWeeks); // Sort by most missed weeks first
  }, [reports, users, weeks, selectedClient, filteredReports]);

  // Filter complianceData based on selectedUser
  const filteredComplianceData = useMemo(() => {
    if (selectedUser === "all") {
      return complianceData;
    } else {
      return complianceData.filter(
        (userData) => userData.user.email === selectedUser,
      );
    }
  }, [complianceData, selectedUser]);

  // Safe date formatting function
  const formatDate = (
    dateString: string | undefined,
    formatStr: string = "MMM d, yyyy",
  ): string => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      return date.toString() !== "Invalid Date"
        ? format(date, formatStr)
        : "Invalid date";
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  // Handle keyboard navigation for the dropdown
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setShowDropdown(false);
    } else if (
      e.key === "Enter" &&
      showDropdown &&
      filteredUserOptions.length > 0
    ) {
      const firstUser = filteredUserOptions[0];
      setSelectedUser(firstUser.email);
      setShowDropdown(false);
      setUserSearchQuery(firstUser.name || firstUser.email);
    }
  };

  // Helper function to check if a reminder has been sent
  const hasReminderBeenSent = (
    userEmail: string,
    weekLabel: string,
  ): boolean => {
    const reminderKey = `${userEmail}-${weekLabel}`;
    return sentReminders.has(reminderKey);
  };

  // Helper function to get reminder key
  const getReminderKey = (userEmail: string, weekLabel: string): string => {
    return `${userEmail}-${weekLabel}`;
  };

  // Debug function to analyze report matching
  const debugReportMatching = (report: any, week: WeekData) => {
    const debug = {
      reportId: report.id,
      reportDate: report.date,
      reportSubject: report.subject,
      reportDateRange: report.dateRange,
      reportCreatedAt: report.createdAt,
      weekLabel: week.label,
      weekStart: week.startDate,
      weekEnd: week.endDate,
      matches: {
        byDate: false,
        bySubject: false,
        byDateRange: false,
        byCreatedAt: false,
      },
    };

    // Check by date
    if (report.date) {
      try {
        const reportDate = new Date(report.date);
        debug.matches.byDate =
          !isNaN(reportDate.getTime()) &&
          isWithinInterval(reportDate, {
            start: week.startDate,
            end: week.endDate,
          });
      } catch (error) {
        console.warn("Error parsing report date:", report.date, error);
      }
    }

    // Check by subject
    if (report.subject) {
      const subject = report.subject.toLowerCase();
      const weekLabel = week.label.toLowerCase();
      debug.matches.bySubject =
        subject.includes(weekLabel) ||
        subject.includes(format(week.startDate, "MMM d").toLowerCase()) ||
        subject.includes(format(week.endDate, "MMM d").toLowerCase());
    }

    // Check by dateRange
    if (report.dateRange) {
      try {
        const dateRange = report.dateRange;
        if (typeof dateRange === "string") {
          const dateMatch = dateRange.match(
            /(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})/g,
          );
          if (dateMatch && dateMatch.length >= 2) {
            const startDate = new Date(dateMatch[0]);
            const endDate = new Date(dateMatch[1]);
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
              debug.matches.byDateRange =
                isWithinInterval(week.startDate, {
                  start: startDate,
                  end: endDate,
                }) ||
                isWithinInterval(week.endDate, {
                  start: startDate,
                  end: endDate,
                });
            }
          }
        }
      } catch (error) {
        console.warn("Error parsing dateRange:", report.dateRange, error);
      }
    }

    // Check by createdAt
    if (report.createdAt) {
      try {
        const createdDate = new Date(report.createdAt);
        debug.matches.byCreatedAt =
          !isNaN(createdDate.getTime()) &&
          isWithinInterval(createdDate, {
            start: week.startDate,
            end: week.endDate,
          });
      } catch (error) {
        console.warn("Error parsing createdAt date:", report.createdAt, error);
      }
    }

    return debug;
  };

  // Function to manually assign report to week
  const manuallyAssignReportToWeek = async (report: any, week: WeekData) => {
    try {
      // Update the report with the correct date to match the week
      const updatedReport = {
        ...report,
        date: format(week.startDate, "yyyy-MM-dd"), // Use week start date
        dateRange: `${format(week.startDate, "MM/dd/yyyy")} - ${format(week.endDate, "MM/dd/yyyy")}`,
        updatedAt: new Date().toISOString(),
      };

      // Save the updated report
      const response = await fetch("/api/reports", {
        method: "POST",
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(updatedReport),
      });

      if (response.ok) {
        toast({
          title: "Report Assigned",
          description: `Report "${report.subject}" has been assigned to week ${week.label}`,
        });
        // Refresh the data
        window.location.reload();
      } else {
        throw new Error("Failed to update report");
      }
    } catch (error) {
      console.error("Error assigning report to week:", error);
      toast({
        title: "Error",
        description: "Failed to assign report to week",
        variant: "destructive",
      });
    }
  };

  // Handle sending a reminder
  const handleSendReminder = async (
    user: { name: string; email: string },
    week: WeekData,
  ) => {
    try {
      setIsSendingReminder(true);

      if (!currentUser) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to send reminders",
          variant: "destructive",
        });
        return;
      }

      // Get valid access token using Gmail service (same as send email)
      let accessToken;
      try {
        accessToken = await gmailService.getValidAccessToken();
      } catch (error) {
        console.error("Error getting Gmail access token:", error);
        toast({
          title: "Gmail Service Error",
          description:
            "Unable to access Gmail service. Please try reconnecting your Gmail account.",
          variant: "destructive",
        });
        return;
      }

      if (!accessToken) {
        toast({
          title: "Gmail Access Required",
          description:
            "Please authenticate with Gmail to send reminder emails. Go to Settings to connect your Gmail account.",
          variant: "destructive",
        });
        return;
      }

      // Create JWT token for authentication (same as other admin functions)
      const token = btoa(JSON.stringify({ email: currentUser.email }));

      // Send reminder via API
      const response = await fetch("/api/admin/send-reminder", {
        method: "POST",
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          userEmail: user.email,
          userName: user.name,
          weekLabel: week.label,
          access_token: accessToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("You do not have permission to send reminders");
        } else if (response.status === 401) {
          throw new Error("Authentication failed. Please log in again.");
        } else if (data.error && data.error.includes("Gmail API error")) {
          throw new Error(
            "Gmail API error. Please check your Gmail connection.",
          );
        } else {
          throw new Error(data.error || "Failed to send reminder");
        }
      }

      setReminderUser(null);

      // Track the sent reminder locally
      const reminderKey = getReminderKey(user.email, week.label);
      setSentReminders((prev) => new Set([...prev, reminderKey]));

      toast({
        title: "✅ Reminder Sent Successfully",
        description: `Reminder email sent to ${user.name} (${user.email}) for ${week.label}. The user will receive the email shortly.`,
      });
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast({
        title: "❌ Failed to Send Reminder",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSendingReminder(false);
    }
  };

  // Filter users based on search query
  const filteredUserOptions = useMemo(() => {
    if (!userSearchQuery) return users;
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        (user.name &&
          user.name.toLowerCase().includes(userSearchQuery.toLowerCase())),
    );
  }, [users, userSearchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentWeek = new Date();
  currentWeek.setHours(0, 0, 0, 0);
  currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay());

  const reportsThisWeek = reports.filter((report) => {
    const reportDate = new Date(report.createdAt);
    return reportDate >= currentWeek;
  });

  const usersWithReports = new Set(reportsThisWeek.map((r) => r.createdBy));

  const totalExpectedReports = users.length * weeks.length;
  const totalSubmissions = complianceData.reduce((sum, userData) => {
    const submissionCount = Object.values(userData.weeks).filter(
      (w) => w.hasSentReport,
    ).length;
    return sum + submissionCount;
  }, 0);
  const totalMissing = complianceData.reduce(
    (sum, userData) => sum + userData.missedWeeks,
    0,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold">Compliance Dashboard</h2>
          <Badge variant="outline" className="text-sm">
            {users.length} Users
          </Badge>
          <Badge variant="outline" className="text-sm">
            {weeks.length} Weeks
          </Badge>
        </div>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search reports by subject or user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 w-64"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDebug(!showDebug)}
          >
            <Search className="w-4 h-4 mr-2" />
            Debug Matching
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsClientSearchOpen(!isClientSearchOpen)}
            ref={clientDropdownRef}
          >
            <Users className="w-4 h-4 mr-2" />
            Filter by Client
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h3 className="text-sm font-medium mb-2">Date Range</h3>
            <div className="flex gap-2">
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate
                      ? format(startDate, "MMM d, yyyy")
                      : "Select Start Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date || undefined);
                      setShowDatePicker(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                onClick={() => {
                  setStartDate(undefined);
                  setShowDatePicker(false);
                }}
              >
                Reset
              </Button>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium mb-2">Time Period</h3>
            <div className="flex gap-2">
              <Button
                variant={weeksToShow === 4 ? "default" : "outline"}
                onClick={() => setWeeksToShow(4)}
              >
                4 Weeks
              </Button>
              <Button
                variant={weeksToShow === 8 ? "default" : "outline"}
                onClick={() => setWeeksToShow(8)}
              >
                8 Weeks
              </Button>
              <Button
                variant={weeksToShow === 12 ? "default" : "outline"}
                onClick={() => setWeeksToShow(12)}
              >
                12 Weeks
              </Button>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium mb-2">Show Sent Only</h3>
            <div className="flex gap-2 items-center">
              <Switch
                checked={showSentOnly}
                onCheckedChange={setShowSentOnly}
                id="sent-only"
              />
              <Label htmlFor="sent-only" className="cursor-pointer">
                {showSentOnly ? "Showing sent" : "Showing all"}
              </Label>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium mb-2">Filter by User</h3>
          <div className="relative" ref={dropdownRef}>
            <Input
              type="text"
              placeholder="Search users..."
              value={showDropdown ? userSearchQuery : selectedUserDisplay}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              onClick={() => setShowDropdown(true)}
              onKeyDown={handleKeyDown}
              className="w-full md:w-[300px] mb-1"
            />
            {showDropdown && (
              <div className="absolute z-50 w-full md:w-[300px] mt-1 border rounded-md bg-background shadow-md max-h-60 overflow-auto">
                <div className="p-2 border-b">
                  <div
                    className={`px-2 py-1 cursor-pointer hover:bg-secondary/20 rounded ${selectedUser === "all" ? "bg-secondary/40" : ""}`}
                    onClick={() => {
                      setSelectedUser("all");
                      setShowDropdown(false);
                      setUserSearchQuery("");
                    }}
                  >
                    All Users
                  </div>
                </div>
                <div className="p-1">
                  {filteredUserOptions.length > 0 ? (
                    filteredUserOptions.map((user) => (
                      <div
                        key={user.email}
                        className={`px-2 py-1 cursor-pointer hover:bg-secondary/20 rounded ${selectedUser === user.email ? "bg-secondary/40" : ""}`}
                        onClick={() => {
                          setSelectedUser(user.email);
                          setShowDropdown(false);
                          setUserSearchQuery(user.name || user.email);
                        }}
                      >
                        <div className="font-medium">
                          {user.name || user.email}
                        </div>
                        {user.name && (
                          <div className="text-xs text-muted-foreground">
                            {user.email}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-center text-muted-foreground">
                      No users found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium mb-2">Filter by Recipients</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label
                htmlFor="to-filter"
                className="text-xs text-muted-foreground"
              >
                TO Recipients
              </Label>
              <Input
                id="to-filter"
                placeholder="Filter by TO email..."
                value={toRecipient}
                onChange={(e) => setToRecipient(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label
                htmlFor="cc-filter"
                className="text-xs text-muted-foreground"
              >
                CC Recipients
              </Label>
              <Input
                id="cc-filter"
                placeholder="Filter by CC email..."
                value={ccRecipient}
                onChange={(e) => setCcRecipient(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label
                htmlFor="bcc-filter"
                className="text-xs text-muted-foreground"
              >
                BCC Recipients
              </Label>
              <Input
                id="bcc-filter"
                placeholder="Filter by BCC email..."
                value={bccRecipient}
                onChange={(e) => setBccRecipient(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium mb-2">Filter by Client</h3>
          <div className="relative" ref={clientDropdownRef}>
            <Input
              placeholder="Search clients or view all reports for a client..."
              value={clientSearchQuery}
              onChange={(e) => setClientSearchQuery(e.target.value)}
              onClick={() => setIsClientSearchOpen(true)}
              className="w-full md:w-[300px] mb-1"
            />
            <div className="absolute right-2 top-2.5 flex items-center">
              <Search className="h-4 w-4 text-muted-foreground mr-2" />
              {selectedClient !== "all" && (
                <X
                  className="h-4 w-4 cursor-pointer text-muted-foreground"
                  onClick={() => {
                    setSelectedClient("all");
                    setClientSearchQuery("");
                  }}
                />
              )}
            </div>

            {(filteredClients.length > 0 || selectedClient !== "all") &&
              isClientSearchOpen && (
                <div className="absolute z-50 w-full md:w-[300px] mt-1 border rounded-md bg-background shadow-md max-h-60 overflow-auto">
                  <div className="p-2 border-b">
                    <div
                      className={`px-2 py-1 cursor-pointer hover:bg-secondary/20 rounded ${selectedClient === "all" ? "bg-secondary/40" : ""}`}
                      onClick={() => {
                        setSelectedClient("all");
                        setIsClientSearchOpen(false);
                        setClientSearchQuery("");
                      }}
                    >
                      All Clients
                    </div>
                  </div>
                  <div className="p-1">
                    {filteredClients.length > 0 ? (
                      filteredClients.map((client) => (
                        <div
                          key={client}
                          className={`px-2 py-1 cursor-pointer hover:bg-secondary/20 rounded ${selectedClient === client ? "bg-secondary/40" : ""}`}
                          onClick={() => {
                            setSelectedClient(client);
                            setIsClientSearchOpen(false);
                            setClientSearchQuery(client);
                          }}
                        >
                          <div className="font-medium">{client}</div>
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-center text-muted-foreground">
                        No clients found
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Client filtering active notice */}
      {selectedClient !== "all" && (
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="px-3 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mr-3">
                  {selectedClient}
                </div>
                <span className="text-blue-800">
                  Showing all reports for this client across all users
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-blue-300 hover:bg-blue-200"
                onClick={() => {
                  setSelectedClient("all");
                  setClientSearchQuery("");
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Clear client filter
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Debug Panel */}
      {showDebug && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Search className="w-4 h-4 mr-2" />
              Debug Report Matching
            </CardTitle>
            <CardDescription>
              Analyze why reports might not be matching weeks correctly
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">
                    Sample Reports Analysis
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {(searchTerm ? filteredReports : reports)
                      .slice(0, 5)
                      .map((report, index) => (
                        <div key={index} className="p-2 border rounded text-xs">
                          <div>
                            <strong>ID:</strong> {report.id}
                          </div>
                          <div>
                            <strong>Date:</strong> {report.date || "N/A"}
                          </div>
                          <div>
                            <strong>Subject:</strong> {report.subject || "N/A"}
                          </div>
                          <div>
                            <strong>DateRange:</strong>{" "}
                            {report.dateRange || "N/A"}
                          </div>
                          <div>
                            <strong>CreatedAt:</strong>{" "}
                            {report.createdAt || "N/A"}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Week Definitions</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {weeks.slice(0, 5).map((week, index) => (
                      <div key={index} className="p-2 border rounded text-xs">
                        <div>
                          <strong>Label:</strong> {week.label}
                        </div>
                        <div>
                          <strong>Start:</strong>{" "}
                          {format(week.startDate, "MMM d, yyyy")}
                        </div>
                        <div>
                          <strong>End:</strong>{" "}
                          {format(week.endDate, "MMM d, yyyy")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Matching Analysis</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(searchTerm ? filteredReports : reports)
                    .slice(0, 3)
                    .map((report, reportIndex) =>
                      weeks.slice(0, 3).map((week, weekIndex) => {
                        const debug = debugReportMatching(report, week);
                        const hasMatch = Object.values(debug.matches).some(
                          (match) => match,
                        );
                        return (
                          <div
                            key={`${reportIndex}-${weekIndex}`}
                            className={`p-2 border rounded text-xs ${hasMatch ? "bg-green-50" : "bg-red-50"}`}
                          >
                            <div>
                              <strong>Report {report.id}</strong> vs{" "}
                              <strong>Week {week.label}</strong>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                              <div>
                                By Date: {debug.matches.byDate ? "✅" : "❌"}
                              </div>
                              <div>
                                By Subject:{" "}
                                {debug.matches.bySubject ? "✅" : "❌"}
                              </div>
                              <div>
                                By DateRange:{" "}
                                {debug.matches.byDateRange ? "✅" : "❌"}
                              </div>
                              <div>
                                By CreatedAt:{" "}
                                {debug.matches.byCreatedAt ? "✅" : "❌"}
                              </div>
                            </div>
                            {!hasMatch && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-2 text-xs"
                                onClick={() =>
                                  manuallyAssignReportToWeek(report, week)
                                }
                              >
                                Assign to Week
                              </Button>
                            )}
                          </div>
                        );
                      }),
                    )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reports</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {selectedUser === "all"
                ? selectedClient === "all"
                  ? reports.length
                  : reports.filter((r) => r.clientName === selectedClient)
                      .length
                : selectedClient === "all"
                  ? reports.filter((r) => r.createdBy === selectedUser).length
                  : reports.filter(
                      (r) =>
                        r.createdBy === selectedUser &&
                        r.clientName === selectedClient,
                    ).length}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedUser === "all"
                ? selectedClient === "all"
                  ? "All time reports"
                  : `Reports for ${selectedClient}`
                : selectedClient === "all"
                  ? `Reports by ${selectedUser}`
                  : `Reports by ${selectedUser} for ${selectedClient}`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Compliance Rate
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {selectedUser === "all"
                ? `${Math.round((totalSubmissions / (totalExpectedReports || 1)) * 100)}%`
                : filteredComplianceData.length > 0
                  ? `${Math.round(((weeks.length - filteredComplianceData[0].missedWeeks) / weeks.length) * 100)}%`
                  : "0%"}
            </div>
            <p className="text-xs text-muted-foreground">
              Overall compliance rate
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Missing Reports
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {selectedUser === "all"
                ? totalMissing
                : filteredComplianceData.find(
                    (u) => u.user.email === selectedUser,
                  )?.missedWeeks || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedUser === "all"
                ? "Total missing reports"
                : "Missing reports for selected period"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {selectedUser === "all" ? users.length : 1}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedUser === "all" ? "Total active users" : "Selected user"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Compliance</CardTitle>
          <CardDescription>
            Showing compliance for{" "}
            {selectedUser === "all" ? "all users" : "user: " + selectedUser}
            {selectedClient !== "all" && ` (client: ${selectedClient})`}
            {showSentOnly && " (sent reports only)"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredComplianceData
              // When a client is selected, only show users that have reports for that client
              .filter((userData) => {
                if (selectedClient === "all") return true;

                // Check if this user has any reports for the selected client
                const userReports = reports.filter(
                  (r) =>
                    r.createdBy === userData.user.email &&
                    r.clientName === selectedClient,
                );
                return userReports.length > 0;
              })
              .map((userData) => {
                // Get unique clients for this user
                const userReports = reports.filter(
                  (r) => r.createdBy === userData.user.email,
                );

                // Filter by client if needed
                const filteredUserReports =
                  selectedClient === "all"
                    ? userReports
                    : userReports.filter(
                        (r) => r.clientName === selectedClient,
                      );

                const userClients = new Set(
                  filteredUserReports
                    .filter((report) => report.clientName)
                    .map((report) => report.clientName),
                );

                // Count sent reports
                const sentReports = filteredUserReports.filter(
                  (r) => r.sentByEmail === true,
                ).length;

                // Skip users with no reports for selected client
                if (
                  selectedClient !== "all" &&
                  filteredUserReports.length === 0
                ) {
                  return null;
                }

                return (
                  <div
                    key={userData.user.email}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        {userData.user.email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{userData.user.email}</p>
                        <div className="text-sm text-muted-foreground flex gap-2">
                          <span>Total: {filteredUserReports.length}</span>
                          {sentReports > 0 && (
                            <span className="text-blue-600 font-medium">
                              <Mail className="inline h-3 w-3 mr-1" />
                              Sent: {sentReports}
                            </span>
                          )}
                        </div>
                        {userClients.size > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Array.from(userClients)
                              .slice(0, 3)
                              .map((client) => (
                                <span
                                  key={`${client}-${userData.user.email}`}
                                  className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium"
                                >
                                  {client}
                                </span>
                              ))}
                            {userClients.size > 3 && (
                              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                                +{userClients.size - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          userData.missedWeeks === 0 ? "default" : "destructive"
                        }
                      >
                        {userData.missedWeeks} missed
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedUser(userData.user.email)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Compliance Details</CardTitle>
          <CardDescription>
            {selectedUser === "all"
              ? selectedClient === "all"
                ? "Select a specific user to view detailed weekly compliance"
                : `Showing all users' reports for client: ${selectedClient}`
              : `Weekly compliance details for ${selectedUser} ${showSentOnly ? "(sent reports only)" : ""} ${selectedClient !== "all" ? `(all reports for client: ${selectedClient})` : ""}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {selectedUser !== "all" && filteredComplianceData.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Week</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {weeks.map((week) => {
                  const weekKey = format(week.startDate, "yyyy-MM-dd");
                  const userData = filteredComplianceData[0];
                  const weekData = userData.weeks[weekKey];

                  // Skip non-sent reports if showSentOnly is true
                  if (
                    showSentOnly &&
                    !weekData?.reports?.some((r) => r.sentByEmail === true)
                  ) {
                    return null;
                  }

                  // If filtering by client, we'll still show the week but highlight the selected client's reports
                  let showWeek = true;
                  if (selectedClient !== "all") {
                    // Only show weeks that have at least one report for the selected client
                    showWeek = weekData?.reports?.some(
                      (r) => r.clientName === selectedClient,
                    );
                    if (!showWeek) {
                      return null;
                    }
                  }

                  return (
                    <TableRow key={`${weekKey}-${selectedClient}`}>
                      <TableCell>{week.label}</TableCell>
                      <TableCell>
                        {weekData?.hasSentReport &&
                        weekData?.reports &&
                        weekData.reports.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {weekData.reports
                              .filter((report) => report.clientName)
                              .map((report, idx) => (
                                <span
                                  key={`${report.id || idx}-${idx}`}
                                  className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium"
                                >
                                  {report.clientName}
                                </span>
                              ))}
                            {weekData.reports.filter(
                              (report) => !report.clientName,
                            ).length > 0 && (
                              <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">
                                No client (
                                {
                                  weekData.reports.filter(
                                    (report) => !report.clientName,
                                  ).length
                                }
                                )
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {weekData?.hasSentReport ? (
                          <div className="flex flex-col gap-1">
                            {weekData.reports
                              .sort((a, b) =>
                                a.sentByEmail === b.sentByEmail
                                  ? 0
                                  : a.sentByEmail
                                    ? -1
                                    : 1,
                              )
                              .map((report, idx) => (
                                <Badge
                                  key={`${report.id || idx}-${idx}`}
                                  variant="default"
                                  className={
                                    report.sentByEmail === true
                                      ? "bg-blue-500 hover:bg-blue-600"
                                      : "bg-green-500 hover:bg-green-600"
                                  }
                                >
                                  {report.sentByEmail === true ? (
                                    <>
                                      <Mail className="mr-1 h-3 w-3" /> Sent
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="mr-1 h-3 w-3" />{" "}
                                      Draft
                                    </>
                                  )}
                                </Badge>
                              ))}
                          </div>
                        ) : (
                          <Badge variant="destructive">
                            <X className="mr-1 h-3 w-3" />
                            Missing
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {weekData?.hasSentReport &&
                        weekData.reports.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {weekData.reports.map((report, idx) => (
                              <TableRow key={`${report.id || idx}-${idx}`}>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center"
                                    onClick={() =>
                                      setSelectedReport(report as WeeklyReport)
                                    }
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View{" "}
                                    {report.clientName &&
                                      `(${report.clientName})`}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center"
                            onClick={() => {
                              setReminderUser({
                                user: {
                                  email: userData.user.email,
                                  name:
                                    userData.user.name || userData.user.email,
                                },
                                week,
                              });
                            }}
                            disabled={
                              isSendingReminder ||
                              hasReminderBeenSent(
                                userData.user.email,
                                week.label,
                              )
                            }
                            title={
                              hasReminderBeenSent(
                                userData.user.email,
                                week.label,
                              )
                                ? "Reminder already sent for this week"
                                : "Send reminder email for missing report"
                            }
                          >
                            {hasReminderBeenSent(
                              userData.user.email,
                              week.label,
                            ) ? (
                              <>
                                <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                                <span className="text-green-600 font-medium">
                                  Sent reminder
                                </span>
                              </>
                            ) : (
                              <>
                                <Mail className="mr-2 h-4 w-4" />
                                Remind
                              </>
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {selectedUser === "all" && selectedClient !== "all" && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Week</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports
                  .filter((report) => report.clientName === selectedClient)
                  .sort((a, b) => {
                    // Sort by user first, then by date (newest first)
                    if (a.createdBy !== b.createdBy) {
                      return a.createdBy.localeCompare(b.createdBy);
                    }
                    return (
                      new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                  })
                  .map((report, idx) => {
                    // Skip if showSentOnly is true and the report is not sent
                    if (showSentOnly && !report.sentByEmail) {
                      return null;
                    }

                    // Find which week this report belongs to
                    const reportDate = new Date(report.date);
                    let reportWeek = null;

                    // Try multiple methods to find the matching week
                    if (report.date) {
                      try {
                        const parsedDate = new Date(report.date);
                        if (!isNaN(parsedDate.getTime())) {
                          reportWeek = weeks.find((week) =>
                            isWithinInterval(parsedDate, {
                              start: week.startDate,
                              end: week.endDate,
                            }),
                          );
                        }
                      } catch (error) {
                        console.warn(
                          "Error parsing report date for week matching:",
                          report.date,
                          error,
                        );
                      }
                    }

                    // If not found by date, try by subject
                    if (!reportWeek && report.subject) {
                      const subject = report.subject.toLowerCase();
                      reportWeek = weeks.find((week) => {
                        const weekLabel = week.label.toLowerCase();
                        return (
                          subject.includes(weekLabel) ||
                          subject.includes(
                            format(week.startDate, "MMM d").toLowerCase(),
                          ) ||
                          subject.includes(
                            format(week.endDate, "MMM d").toLowerCase(),
                          )
                        );
                      });
                    }

                    // If still not found, try by dateRange
                    if (!reportWeek && report.dateRange) {
                      try {
                        const dateRange = report.dateRange;
                        if (typeof dateRange === "string") {
                          const dateMatch = dateRange.match(
                            /(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})/g,
                          );
                          if (dateMatch && dateMatch.length >= 2) {
                            const startDate = new Date(dateMatch[0]);
                            const endDate = new Date(dateMatch[1]);
                            if (
                              !isNaN(startDate.getTime()) &&
                              !isNaN(endDate.getTime())
                            ) {
                              reportWeek = weeks.find(
                                (week) =>
                                  isWithinInterval(week.startDate, {
                                    start: startDate,
                                    end: endDate,
                                  }) ||
                                  isWithinInterval(week.endDate, {
                                    start: startDate,
                                    end: endDate,
                                  }),
                              );
                            }
                          }
                        }
                      } catch (error) {
                        console.warn(
                          "Error parsing dateRange for week matching:",
                          report.dateRange,
                          error,
                        );
                      }
                    }

                    return (
                      <TableRow key={`${report.id || idx}-${idx}`}>
                        <TableCell>
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mr-2">
                              {report.createdBy.charAt(0).toUpperCase()}
                            </div>
                            <span>{report.createdBy}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {reportWeek
                            ? reportWeek.label
                            : format(reportDate, "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="default"
                            className={
                              report.sentByEmail === true
                                ? "bg-blue-500 hover:bg-blue-600"
                                : "bg-green-500 hover:bg-green-600"
                            }
                          >
                            {report.sentByEmail === true ? (
                              <>
                                <Mail className="mr-1 h-3 w-3" /> Sent
                              </>
                            ) : (
                              <>
                                <CheckCircle className="mr-1 h-3 w-3" /> Draft
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center"
                            onClick={() =>
                              setSelectedReport(report as WeeklyReport)
                            }
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Report
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}

          {selectedUser === "all" && selectedClient === "all" && (
            <div className="text-center py-8 text-muted-foreground">
              Select a specific user to view their detailed compliance data
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedReport}
        onOpenChange={(isOpen) => !isOpen && setSelectedReport(null)}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedReport?.subject || "Report Details"}
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium">Created By</h3>
                  <p className="text-muted-foreground">
                    {selectedReport.createdBy}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Date</h3>
                  <p className="text-muted-foreground">
                    {selectedReport.date
                      ? formatDate(selectedReport.date)
                      : "Not specified"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Status</h3>
                  <Badge
                    variant={
                      selectedReport.sentByEmail === true
                        ? "default"
                        : "secondary"
                    }
                  >
                    {selectedReport.sentByEmail === true ? "Sent" : "Draft"}
                  </Badge>
                </div>
                {selectedReport.clientName && (
                  <div>
                    <h3 className="text-sm font-medium">Client</h3>
                    <p className="text-muted-foreground">
                      <span className="inline-flex px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                        {selectedReport.clientName}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {selectedReport.last_week_tasks &&
                selectedReport.last_week_tasks.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-1">
                      Last Week Tasks
                    </h3>
                    <ul className="list-disc pl-5 space-y-1">
                      {selectedReport.last_week_tasks.map((task, index) => (
                        <li key={index} className="text-sm">
                          {typeof task === "string" ? task : task.value}
                          {task.dependency && (
                            <span className="text-xs text-muted-foreground ml-2">
                              Depends on: {task.dependency}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {selectedReport.next_week_tasks &&
                selectedReport.next_week_tasks.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-1">
                      Next Week Tasks
                    </h3>
                    <ul className="list-disc pl-5 space-y-1">
                      {selectedReport.next_week_tasks.map((task, index) => (
                        <li key={index} className="text-sm">
                          {typeof task === "string" ? task : task.value}
                          {task.dependency && (
                            <span className="text-xs text-muted-foreground ml-2">
                              Depends on: {task.dependency}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {selectedReport.blockers &&
                selectedReport.blockers.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-1">Blockers</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      {selectedReport.blockers.map((item, index) => (
                        <li key={index} className="text-sm">
                          {typeof item === "string" ? item : item.value}
                          {item.dependency && (
                            <span className="text-xs text-muted-foreground ml-2">
                              Depends on: {item.dependency}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog
        open={!!reminderUser}
        onOpenChange={(isOpen) => !isOpen && setReminderUser(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Reminder</DialogTitle>
          </DialogHeader>
          {reminderUser && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-1">User</h3>
                <p className="text-muted-foreground">
                  {reminderUser.user.name} ({reminderUser.user.email})
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium mb-1">Week</h3>
                <p className="text-muted-foreground">
                  {reminderUser.week.label}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium mb-1">Message</h3>
                <p className="text-muted-foreground">
                  A reminder will be sent to the user about submitting their
                  weekly report for the week of {reminderUser.week.label}.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setReminderUser(null)}
                  disabled={isSendingReminder}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    handleSendReminder(reminderUser.user, reminderUser.week);
                  }}
                  disabled={isSendingReminder}
                >
                  {isSendingReminder ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send Reminder
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportComplianceDashboard;
